// eslint-disable-next-line no-restricted-imports
import {
  GoogleTagManager as GTM,
  sendGTMEvent as internalSendGTMEvent,
} from '@next/third-parties/google';
import { useEffect, useMemo } from 'react';

import { Viewer } from '@vimeo/viewer';

import getEnvironment from '../get-environment';

// Using the existing GTM data layer name in order to maintain backward
// compatibility with the existing GTM.trackEvent calls in the codebase.
const DATA_LAYER_NAME = '_gtm';

const SINGULAR_STORAGE_KEY = 'vimeo_8bd2567c_vimeows_global_properties';

const env = getEnvironment();

export function getInitialGTMDataLayer(viewer?: Viewer | null) {
  if (!viewer || viewer.isThirdPartyTrackingEnabled === false) {
    return {};
  }

  // Base data
  const dataLayer = {
    language: viewer.locale,
    user_status: viewer.user ? 'logged_in' : 'logged_out',
    user_type: !viewer.user ? 'none' : viewer.user.account,
    vuid: viewer.vuid,
  };

  // Logged in user data
  if (viewer.user) {
    (dataLayer as any)['user_id'] = viewer.user.id;
    (dataLayer as any)['is_free_trial'] = viewer.user.isFreeTrial;
    (dataLayer as any)['new_user'] = viewer.user.isNewUser;
  }

  // Team data
  if (viewer.user?.account === 'enterprise' || viewer.teamUser) {
    (dataLayer as any)['team_subscription_type'] = viewer.teamUser?.accountType;
    (dataLayer as any)['owner_id'] = viewer.teamUser?.ownerId;
  }

  // These environment-specific values are duplicated from config.
  switch (env) {
    case 'production':
      return {
        ...dataLayer,
        ga_universal_id: 'UA-76641-8',
        comscore_site_id: '10348289',
      };
    case 'staging':
    case 'development':
      return {
        ...dataLayer,
        ga_universal_id: 'UA-76641-70',
        comscore_site_id: '10348289',
      };
  }
}

export function GoogleTagManager({ viewer }: { viewer: Viewer }) {
  const dataLayer = useMemo(() => {
    return getInitialGTMDataLayer(viewer);
  }, [viewer]);

  useEffect(() => {
    if (viewer.isThirdPartyTrackingEnabled === false) {
      return;
    }

    if (typeof window !== 'undefined' && !window._gtm) {
      window._gtm = [dataLayer];
    }

    // Originally from google_tag_manager.phtml -
    // https://github.com/vimeows/vimeo/blob/15bb818962500c64feb363c70c9f1f0dfcfbb7f1/views_v6/template/google_tag_manager.phtml#L34-L39
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(
        SINGULAR_STORAGE_KEY,
        JSON.stringify({ user_id: viewer.user?.id, vuid: viewer.vuid }),
      );
    }
  }, [dataLayer, viewer]);

  // These values are from the gtm.script_url in the dev config file
  const gtmAuth = env === 'production' ? '' : 'eSWk7P9f-D6sRBA_SuJKNw';
  const gtmPreview = env === 'production' ? '' : 'env-628';

  if (viewer.isThirdPartyTrackingEnabled === false) {
    return null;
  }

  return (
    <GTM
      gtmId="GTM-RBKK"
      dataLayer={dataLayer}
      dataLayerName={DATA_LAYER_NAME}
      auth={gtmAuth}
      preview={gtmPreview}
    />
  );
}

/**
 * Send an event to Google Tag Manager. Wraps the internal sendGTMEvent
 * function to use the correct data layer name by default.
 */
export function sendGTMEvent(data: Record<string, unknown>, customDataLayerName?: string) {
  internalSendGTMEvent(data, customDataLayerName || DATA_LAYER_NAME);
}
